import { useMemo } from 'react';
import { ProcessorCapability } from 'lib/graphql';
import { type SpecificationOption } from '../components/specification-option-card';
import { CutsheetWizardFormValues, SpecificationGroup } from '../types';

export function useSpecificationGroups(
  activeCapability: ProcessorCapability | undefined,
  values: CutsheetWizardFormValues,
  setFieldValue: (
    field: string,
    value: unknown,
    shouldValidate?: boolean,
  ) => void,
) {
  const specGroups = useMemo(() => {
    const spec = activeCapability?.activeSpecification;
    const raw = spec?.specifications ?? [];
    // Legacy type: ProcessorCapabilitySpecificationSpecifications
    // We normalize to: { key, title, options: [{value,label}], description }
    return raw
      .map((s) => {
        const key = s?.fid ?? s?.name ?? s?._id ?? '';
        const title = s?.name ?? key;
        const description = s?.description ?? '';
        const measurementUnit = s?.measurementUnit;
        const isRequired = s?.isRequired ?? false;
        const options: SpecificationOption[] = (s?.options ?? [])
          .filter((o) => o?.isVisible !== false)
          .map((v) => ({
            value: String(v?._id ?? v?.name ?? ''),
            label: String(v?.name ?? ''),
            description: v?.description ?? '',
            isDefault: v?.isDefault ?? false,
          }))
          .filter((o: SpecificationOption) => o.value);
        return options.length && s?.isVisible !== false
          ? {
              key,
              title,
              options,
              description,
              measurementUnit,
              isRequired,
              imageUrl: s?.imageUrl,
            }
          : null;
      })
      .filter(Boolean) as SpecificationGroup[];
  }, [activeCapability]);

  const onChangeSpec = (specKey: string) => (value: string) => {
    const newSpecs = {
      ...(values.selectedSpecifications ?? {}),
      [specKey]: value,
    };

    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log('[Debug] Setting specification:', {
        specKey,
        value,
        existingSpecs: values.selectedSpecifications,
        newSpecs,
      });
    }

    return setFieldValue('selectedSpecifications', newSpecs, true);
  };

  return {
    specGroups,
    onChangeSpec,
  };
}
